import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loader2, Trash2, Mail, Phone, MapPin, GraduationCap, BookOpen } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";

const statusColors = {
  new: "bg-yellow-100 text-yellow-800 border-yellow-300",
  contacted: "bg-blue-100 text-blue-800 border-blue-300",
  enrolled: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};

export default function AdmissionsInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const { toast } = useToast();

  const fetch = () => {
    setLoading(true);
    api.get("/admissions").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(fetch, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admissions/${id}/status`, { status });
    toast({ title: "Status updated" });
    fetch();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await api.delete(`/admissions/${id}`);
    toast({ title: "Deleted" });
    fetch();
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div>
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#0B2A6B]">Admissions</h2>
          <p className="text-sm text-slate-500">All admission applications.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "new", "contacted", "enrolled", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition capitalize ${
                filter === s ? "bg-[#0B2A6B] text-white border-[#0B2A6B]" : "bg-white border-slate-200 text-slate-700 hover:border-[#0B2A6B]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {loading ? (
          <div className="lg:col-span-2 py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : filtered.length === 0 ? (
          <div className="lg:col-span-2 py-16 text-center text-slate-400">No applications</div>
        ) : filtered.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="font-bold text-[#0B2A6B] text-lg">{a.full_name}</h3>
                <p className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${statusColors[a.status]}`}>
                {a.status}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-4">
              <a href={`mailto:${a.email}`} className="flex items-center gap-2 text-slate-700 hover:text-yellow-600 truncate">
                <Mail className="h-4 w-4 shrink-0" /><span className="truncate">{a.email}</span>
              </a>
              <a href={`tel:${a.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-yellow-600">
                <Phone className="h-4 w-4" />{a.phone}
              </a>
              <div className="flex items-center gap-2 text-slate-700">
                <GraduationCap className="h-4 w-4" />{a.qualification}
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <BookOpen className="h-4 w-4" />{a.course_interest}
              </div>
              {a.city && (
                <div className="flex items-center gap-2 text-slate-700 col-span-2">
                  <MapPin className="h-4 w-4" />{a.city}
                </div>
              )}
            </div>
            {a.message && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 mb-4">{a.message}</div>
            )}
            <div className="flex items-center gap-2">
              <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="enrolled">Enrolled</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={() => remove(a.id)} className="h-10 w-10 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
